import { isEmpty } from 'lodash/fp';
import { createAction, lt, showInfoAlert } from '@mews/framework';
import { CommanderThunkAction } from '@mews-commander/types';
import { TimeUnit } from '@mews/enums';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { isEnterpriseMultiTaxEnvironment } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { getServiceEnterpriseId, getTimeUnitByServiceId } from '@mews-commander/legacy-entities/src/ducks/services';
import { fetchResources } from '@mews-commander/legacy-entities/src/actions/resources';
import {
    getNextServiceId,
    getDefaultCategoryOccupancies,
    getServiceId,
    isServiceFetched,
    getShowDateServiceDiscrepancyBanner,
} from '../selectors';
import { getSelectedServiceDefaultProductIds, isReservationPlaceCity } from '../selectors/entities';
import { fetchRatePricing, fetchServiceRelatedData } from './api';
import { clearReservations } from './reservations';
import { changeFilters, preselectResourceCategoryType } from './screens';
import { changeTravelAgencyId } from './options';
import {
    RESERVATION_PLACE_SELECTED,
    SERVICE_SELECTED,
    SERVICE_CHANGE_CONFIRMED,
    SERVICE_CHANGE_CANCELLED,
    DEFAULT_OCCUPANCIES_SET,
    TAX_ENVIRONMENT_SET,
    SCREEN_SERVICE_CHANGED,
    SERVICE_FETCHED,
    TIMES_RESET,
} from './types';
import { clearPricing, changeProductIdsWithoutPricingCheck } from './pricing';
import { clearDefaultRates } from './rateGroups';
import { processSelectedReservationPlace } from './availability';

export const confirmServiceChange = (scope: $TSFixMe) => async (dispatch: $TSFixMe, getState: $TSFixMe) => {
    const state = getState();
    const meta = { scope };

    const serviceId = getNextServiceId(state, meta);

    dispatch(clearReservations(scope));
    dispatch(createAction(SERVICE_CHANGE_CONFIRMED, undefined, meta));
    await dispatch(selectService(serviceId, scope));
    dispatch(processSelectedReservationPlace(scope));
    dispatch(fetchRatePricing({ scope }));
};
export const cancelServiceChange = (scope: $TSFixMe) => createAction(SERVICE_CHANGE_CANCELLED, undefined, { scope });

export const serviceChangeReset =
    (scope: $TSFixMe): CommanderThunkAction =>
    (dispatch) => {
        dispatch(createAction(SCREEN_SERVICE_CHANGED, undefined, { scope }));
        dispatch(changeTravelAgencyId(null, scope));
    };

export const selectService =
    (serviceId: string, scope: string, shouldFetchServiceData = false): CommanderThunkAction<Promise<void>> =>
    async (dispatch, getState) => {
        let state = getState();
        const props = { scope };
        const currentServiceId = getServiceId(state, props);
        if (currentServiceId && currentServiceId !== serviceId) {
            dispatch(serviceChangeReset(scope));
        }
        // When we switch from to a non-hourly service we should reset selected times
        const serviceTimeUnit = getTimeUnitByServiceId(state, { serviceId });
        if (serviceTimeUnit !== TimeUnit.Hour) {
            dispatch(createAction(TIMES_RESET, {}, { scope }));
        }

        if (!isServiceFetched(getState(), { serviceId }) || shouldFetchServiceData) {
            return dispatch(fetchServiceRelatedData(serviceId)).then(async () => {
                dispatch(createAction(SERVICE_FETCHED, serviceId));
                await dispatch(selectService(serviceId, scope));
            });
        }

        dispatch(createAction(SERVICE_SELECTED, serviceId, props));
        dispatch(preselectResourceCategoryType({ serviceIds: [serviceId], scope }));

        const enterpriseId = getServiceEnterpriseId(getState(), { id: serviceId });

        if (getCurrentEnterpriseId() !== enterpriseId) {
            await dispatch(fetchResources({ enterpriseId }));
        }

        if (isReservationPlaceCity(getState(), props)) {
            dispatch(createAction(RESERVATION_PLACE_SELECTED, enterpriseId, { scope }));
        }

        // @ts-expect-error $TSFixMeError: Argument of type '{ enterpriseId: any; scope: any; }' is not assignable to parameter of type '{ enterpriseId: string; }'...
        const isMultiTaxEnvironment = isEnterpriseMultiTaxEnvironment(getState(), { enterpriseId, scope });

        dispatch(createAction(TAX_ENVIRONMENT_SET, isMultiTaxEnvironment, { scope }));
        dispatch(clearPricing(scope));
        dispatch(clearDefaultRates(scope));

        // getState() returns different state after the previous dispatches
        state = getState();

        const defaultProductIds = getSelectedServiceDefaultProductIds(state, props);

        if (!isEmpty(defaultProductIds)) {
            dispatch(changeProductIdsWithoutPricingCheck(defaultProductIds, scope));
        }

        // @ts-expect-error
        const defaultOccupancies = getDefaultCategoryOccupancies(state, { scope, serviceId });
        dispatch(createAction(DEFAULT_OCCUPANCIES_SET, defaultOccupancies, props));
        dispatch(changeFilters({ scope }));

        const showAlert = getShowDateServiceDiscrepancyBanner(getState(), { scope, serviceId });
        if (showAlert) {
            showInfoAlert({
                title: lt('SelectDifferentDepartureDate'),
                description: lt('DateServiceDiscrepancyWarningMessage'),
            });
        }
    };
